import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import styled from 'styled-components';
import { LayoutContextProvider } from '../LayoutContext';
import { ANIMATION_DURATION, ANIMATION_TIMING_FUNCTION } from './animationCore';
import Content from './Content';
import Sidebar from './Sidebar';

// Warning: changing the internal spacing and size of this element directly affects the sidebar animation
// e.g. changing the width or the left or right padding/margin values
const StyledMain = styled.div<{ collapsed: boolean }>`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: ${({ collapsed }) => (collapsed ? '80px' : '280px')} 1fr;
  grid-template-rows: 1fr;
  grid-column-gap: 0;
  grid-row-gap: 0;

  transition: grid-template-columns ${ANIMATION_DURATION}ms ${ANIMATION_TIMING_FUNCTION};

  background: ${({ theme }) => theme?.colors?.gray?._25};
`;

const StyledContent = styled(Content)`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;

  background: ${({ theme }) => theme?.colors?.common?.white};
`;

type LayoutDefaultProps = PropsWithChildren<object>;

export default function LayoutDefault({ children }: LayoutDefaultProps) {
  const allowExpandedSidebar = useMedia('(min-width: 1280px)');
  // const collapsedSidebarRoute = useRouteMatch(
  //   routes.filter((route) => route.collapsedSidebar).flatMap((route) => route.path)
  // );
  // Smaller screens and certain routes should have the sidebar collapsed by default
  const defaultCollapsedSidebar = !allowExpandedSidebar;

  // Ref to store the sidebar mode when the app changes to a default collapsed sidebar layout.
  // Initial value depends on the default starting mode of the sidebar.
  const previousSidebarCollapsed = useRef(true); // false = expanded

  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [sidebarTransitionEnded, setSidebarTransitionEnded] = useState(true);

  const toggleSidebarMode = useCallback(() => {
    setSidebarCollapsed((value) => !value);
    setSidebarTransitionEnded(false);
  }, []);

  const handleSetSidebarCollapsed = useCallback((value: boolean) => {
    setSidebarCollapsed((currentValue) => {
      // We should only update the transition state if sidebar is changing mode, otherwise it will break the animation.
      if (currentValue !== value) {
        setSidebarTransitionEnded(false);
      }
      return value;
    });
  }, []);

  const handleTransitionEnd = useCallback(() => {
    setSidebarTransitionEnded(true);
  }, []);

  // The following Effect controls the sidebar mode for the user depending on the app layout.
  // If the app changes to a default collapsed sidebar layout, we automatically collapse the sidebar,
  // if the app exits the default collapsed sidebar layout, we restore the sidebar to the previous mode if the the sidebar is collapsed,
  // because we automatically collapsed for the user.
  // This way we restore the sidebar to expanded if it was expanded before, or keep collapsed if it was collapsed before.
  // If the user manually expands the sidebar in a default collapsed sidebar layout, we keep expanded when the app exits the default collapsed sidebar layout,
  // because the expanded sidebar is the preferable default mode.
  const isInitialMount = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: This effect should only run based on the default app layout changes
  useEffect(
    () => {
      // Only run effect when updating sidebar (after it's mounted), if not,
      // we are not able to expand the sidebar when the app starts in a default collapsed sidebar layout then changes to a normal layout.
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      if (defaultCollapsedSidebar) {
        // We need to store the current sidebar mode to be able to restore it when the app exits the default collapsed sidebar layout.
        previousSidebarCollapsed.current = sidebarCollapsed;
        handleSetSidebarCollapsed(true);
      } else if (sidebarCollapsed) {
        // If sidebar is expanded, we keep expanded. If it is collapsed, we restore to the previous mode.
        handleSetSidebarCollapsed(previousSidebarCollapsed.current);
      }
    },
    // This effect should only run based on the default app layout changes.
    [defaultCollapsedSidebar],
  );
  return (
    <LayoutContextProvider
      value={{
        sidebarCollapsed,
        sidebarTransitionEnded,
        toggleSidebarMode,
        allowExpandedSidebar,
      }}
    >
      <StyledMain
        collapsed={sidebarCollapsed}
        onTransitionEnd={handleTransitionEnd}
      >
        <Sidebar />
        <StyledContent>{children}</StyledContent>
      </StyledMain>
    </LayoutContextProvider>
  );
}
